import { useMemo, useState } from "react";
import { controlToastState, Modal } from "risa-oasis-ui_v2";

import moment from "moment";
import { useSelector } from "react-redux";
import { appendRowDataInGsheet } from "../../../api/postCall/appendRowDataInGsheet";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import { BadgeWithIconProps } from "../../../data-model/badgeWithIconProps";
import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { GsheetDetails } from "../../../enums/gsheetDetails";
import { RootState } from "../../../redux/store/store";
import { getAuthStatusOfMedicalPaOrder } from "../../../utils/getAuthStatusOfMedicalPaOrder";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import CustomCheckBox from "../../customCheckBox/customCheckBox";

interface AuthInaccuracyModalProps {
  badgeList?: BadgeWithIconProps[];
  selectedBadge?: BadgeWithIconProps;
  onClose: () => void;
  orderData?: MedicalPaOrder;
}

const AuthInaccuracyModal = (props: AuthInaccuracyModalProps) => {
  const [selectedBadge] = useState<string>(props?.selectedBadge?.id ?? "");
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [comments, setComments] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );

  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const fieldOptions = useMemo(() => {
    return [
      {
        id: "auth_duration",
        label: "Auth Duration",
      },
      {
        id: "visits",
        label: "Visits",
      },
      {
        id: "other",
        label: "Others",
      },
    ];
  }, []);

  const getAuthStatus = () => {
    return getAuthStatusOfMedicalPaOrder(
      props?.orderData ?? {},
      authStatusOptions,
    ).id;
  };

  const handleSave = async () => {
    setIsLoading(true);
    const dataToSend = {
      order_id: props?.orderData?.id ?? "",
      patient_mrn: props?.orderData?.payload?.patient_id ?? "",
      drug_name: props?.orderData?.payload?.regimen_name ?? "",
      created_at:
        moment(props?.orderData?.created_at).format(" Do MMM YY") ?? "",
      auth_status: getAuthStatus(),
      new_auth_status: selectedBadge ?? "",
      total_visits: "-",
      visits_remaining: "-",
      auth_duration_error: selectedFields.includes("auth_duration")
        ? "Yes"
        : "No",
      visits_error: selectedFields.includes("visits") ? "Yes" : "No",
      other_error: selectedFields.includes("other") ? "Yes" : "No",
      comments: comments,
      reported_by: user?.email ?? "",
      org_id: providerDetails?.FacilityId ?? "",
    };

    apiWorkerManager.enqueueAndProcess({
      patient_id: props?.orderData?.payload?.patient_id ?? "",
      order_id: props?.orderData?.id ?? "",
      org_id: providerDetails?.FacilityId ?? "",
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      event_name: EventsName.SAVE_BUTTON_CLICKED,
      additional_data: {
        screen_name: ScreenNamesForBigQuery.CPT_CODE_SCREEN,
        modal_name: ModalNamesForBigQuery.REPORT_INACCURACY_MODAL,
        auth_status: props?.orderData?.auth_on_file?.auth_status ?? "",
        payer_name: payerName,
      },
    });

    const response = await appendRowDataInGsheet(
      GsheetDetails.MEDICAL_PA_DASHBOARD,
      GsheetDetails.MEDICAL_PA_AUTH_SHEET,
      Object.values(dataToSend),
    );
    if (response.hasError) {
      controlToastState("inaccuracy-reported-failure");
    } else {
      apiWorkerManager.enqueueAndProcess({
        patient_id: props?.orderData?.payload?.patient_id ?? "",
        order_id: props?.orderData?.id ?? "",
        org_id: providerDetails?.FacilityId ?? "",
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        event_name: EventsName.INACCURACY_SAVED,
        additional_data: {
          screen_name: ScreenNamesForBigQuery.CPT_CODE_SCREEN,
          modal_name: ModalNamesForBigQuery.REPORT_INACCURACY_MODAL,
          payer_name: payerName,
        },
      });
      controlToastState("inaccuracy-reported-success");
    }
    setIsLoading(false);
    props.onClose();
  };
  return (
    <Modal
      dialogId="auth-inaccuracy-modal"
      title="Mark Inaccuracy"
      onSave={() => {
        handleSave();
      }}
      saveButtonText={isLoading ? "Saving..." : "Save"}
      onClose={props.onClose}
      onCancel={props.onClose}
      cancelText={"Cancel"}
      disableSave={isLoading}
    >
      <div className="auth-inaccuracy-modal sections flex flex-col gap-3">
        <div className="auth-inaacuracy-modal status-section"></div>
        <div className="auth-inaccuracy-modal fields-section">
          <div className="fields-section header mb-2 text-small font-semibold text-primaryGray-2">
            Which Fields have Inaccuracy
          </div>
          <div className="fields-section fields-list flex flex-col gap-3">
            {fieldOptions.map((field) => (
              <CustomCheckBox
                key={field.id}
                id={field.id}
                label={field.label}
                isChecked={selectedFields.includes(field.id)}
                onCheckBoxValueChange={(data) => {
                  if (data.value) {
                    setSelectedFields([...selectedFields, field.id]);
                  } else {
                    setSelectedFields(
                      selectedFields.filter((id) => id !== field.id),
                    );
                  }
                  apiWorkerManager.enqueueAndProcess({
                    patient_id: props?.orderData?.payload?.patient_id ?? "",
                    order_id: props?.orderData?.id ?? "",
                    org_id: providerDetails?.FacilityId ?? "",
                    user_id: user?.id ?? "",
                    user_email: user?.email ?? "",
                    event_name:
                      EventsName.CHECK_BOX_CLICKED +
                      " (" +
                      field.label +
                      " - " +
                      data.value +
                      ")",
                    additional_data: {
                      screen_name: ScreenNamesForBigQuery.CPT_CODE_SCREEN,
                      modal_name: ModalNamesForBigQuery.REPORT_INACCURACY_MODAL,
                      payer_name: payerName,
                    },
                  });
                }}
              />
            ))}
          </div>
        </div>
        <div className="auth-inaccuracy-modal comments-section">
          <div className="comments-section header mb-2 text-tiny font-normal text-primaryGray-2">
            Comments
          </div>
          <div className="comments-section comments-textarea">
            <textarea
              className="h-20 w-full rounded border border-gray-300 p-2 text-tiny font-normal"
              placeholder="Enter your comments here"
              onChange={(e) => setComments(e.target.value)}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AuthInaccuracyModal;
